/**
 * URLキャプチャのジョブ投入を、生成キュー (GenerationProvider) とトースト・クエリキャッシュに繋いだ形で返す。
 * 生成キューの無い画面では null (入口を出さない)。docs/features/swipe-motion-clip-plan.md §11.2.8。
 */
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useGenerationJobsSafe } from '../../components/generation/state/jobsContext';
import { useToast } from '../../hooks/useToast';
import { urlCaptureRequestErrorMessage, type UrlCaptureJobBody } from './api';
import { startUrlCaptureJob, type StartedUrlCaptureJob } from './captureJob';

export interface UrlCaptureStarter {
  /** 投入してキューに載せる。投入のエラーは利用者向け文言の Error で投げる */
  start: (body: UrlCaptureJobBody) => Promise<StartedUrlCaptureJob>;
}

export function useUrlCaptureStarter(): UrlCaptureStarter | null {
  const jobs = useGenerationJobsSafe();
  const toast = useToast();
  const queryClient = useQueryClient();
  const setActiveJobs = jobs?.setActiveJobs;
  const setShowJobQueueModal = jobs?.setShowJobQueueModal;
  return useMemo(() => {
    if (!setActiveJobs || !setShowJobQueueModal) return null;
    return {
      start: async (body: UrlCaptureJobBody) => {
        try {
          return await startUrlCaptureJob({ setActiveJobs, setShowJobQueueModal, toast, queryClient }, body);
        } catch (err) {
          throw new Error(urlCaptureRequestErrorMessage(err));
        }
      },
    };
  }, [setActiveJobs, setShowJobQueueModal, toast, queryClient]);
}
